import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useQueries, useQueryClient } from "react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock,
  CreditCard,
  FileSignature,
  MessageSquare,
  Package,
  Phone,
  ShieldCheck,
  User,
} from "lucide-react";

import EditModal from "../../components/editModal/editModal.js";
import ContractModalContent from "../../components/contractModalContent/contractModalContent.js";
import ChecklistForm from "../../components/forms/checklistForm.js";
import NotesForm from "../../components/forms/notesForm.js";
import { useAvailability } from "../../hooks/useAvailability.js";
import { useBooking } from "../../hooks/useBooking.js";
import { useBookingData } from "../../hooks/useBookingData.js";
import { useExtras } from "../../hooks/useExtras.js";
import * as helpers from "../../utils/utils.js";
import "./DashBoard.css";

const DAY_MS = 24 * 60 * 60 * 1000;
const BASE_COST_KEYS = new Set(["deposit", "remaining", "balance", "gas", "misc"]);

const getTodayTimestamp = () => {
  const today = new Date();
  return Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
};

const getDayFromSearchParams = (searchParams) => {
  const day = Number(searchParams.get("day"));
  return Number.isFinite(day) && day > 0 ? day : getTodayTimestamp();
};

const addDays = (timestamp, days) => timestamp + days * DAY_MS;

const formatDate = (timestamp) =>
  new Date(timestamp).toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });

const formatShortDate = (timestamp) =>
  new Date(timestamp).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });

const formatMoney = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

const roundCurrency = (value) => Math.round(Number(value || 0) * 100) / 100;

const roundQuantity = (value) => Math.round(Number(value || 0) * 1000) / 1000;

const formatLabel = (value) => {
  if (!value) return "";

  return String(value)
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const getTimeRange = (price) => {
  if (!price?.startTime || !price?.duration) return "Time not set";

  const start = new Date();
  start.setHours(price.startTime.hours || 0, price.startTime.minutes || 0, 0, 0);

  const end = new Date(start.getTime() + Number(price.duration || 0) * 60000);
  const options = { hour: "numeric", minute: "2-digit" };

  return `${start.toLocaleTimeString("en-US", options)} - ${end.toLocaleTimeString("en-US", options)}`;
};

const getPaymentSummary = (costs = {}) => {
  return Object.values(costs).reduce(
    (summary, cost) => {
      const amount = Number(cost?.amount || 0);
      const paid = Number(cost?.paid || 0);

      return {
        total: summary.total + amount,
        paid: summary.paid + paid,
        due: summary.due + Math.max(amount - paid, 0),
      };
    },
    { total: 0, paid: 0, due: 0 }
  );
};

const getSelectedAddons = (booking, extras = []) => {
  const costKeys = Object.keys(booking?.costs || {});
  const extrasByName = new Map(extras.map((extra) => [extra.name, extra]));

  return costKeys
    .filter((key) => {
      if (extrasByName.size > 0) return extrasByName.has(key);
      return !BASE_COST_KEYS.has(key);
    })
    .map((key) => extrasByName.get(key)?.name || formatLabel(key));
};

const normalizeConfigName = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const getFuelPriceConfig = (extras = []) => {
  const exactMatch = extras.find((extra) => {
    const name = normalizeConfigName(extra.name);
    const id = normalizeConfigName(extra.id);
    return name === "fuel" || name === "gas" || id === "fuel" || id === "gas";
  });

  if (exactMatch) return exactMatch;

  return extras.find((extra) => {
    const name = normalizeConfigName(extra.name);
    const id = normalizeConfigName(extra.id);
    return name.includes("fuel") || name.includes("gas") || id.includes("fuel") || id.includes("gas");
  });
};

const getStoredFuelPrice = (fuelCost = {}) => {
  const value = Number(
    fuelCost.pricePerGallon ?? fuelCost.unitPrice ?? fuelCost.price ?? fuelCost.perGallon
  );
  return Number.isFinite(value) && value >= 0 ? value : null;
};

const getFuelPricePerGallon = (fuelCost = {}, fuelPriceConfig) => {
  const storedPrice = getStoredFuelPrice(fuelCost);
  if (storedPrice !== null) return storedPrice;

  const gallons = Number(fuelCost.gallons ?? fuelCost.quantity ?? fuelCost.numUnits);
  const amount = Number(fuelCost.amount);
  if (Number.isFinite(gallons) && gallons > 0 && Number.isFinite(amount) && amount >= 0) {
    return roundCurrency(amount / gallons);
  }

  const databasePrice = Number(fuelPriceConfig?.price);
  return Number.isFinite(databasePrice) && databasePrice >= 0 ? databasePrice : 0;
};

const getFuelGallons = (fuelCost = {}, pricePerGallon = 0) => {
  const storedGallons = Number(fuelCost.gallons ?? fuelCost.quantity ?? fuelCost.numUnits);
  if (Number.isFinite(storedGallons) && storedGallons >= 0) return storedGallons;

  const amount = Number(fuelCost.amount);
  if (Number.isFinite(amount) && amount > 0 && Number(pricePerGallon) > 0) {
    return roundQuantity(amount / pricePerGallon);
  }

  return "";
};

const isChecklistSigned = (booking) => {
  return Boolean(
    booking?.checklist?.status === "completed" ||
      booking?.checklist?.completedAt ||
      booking?.checklist?.id
  );
};

const getSlotPosition = (booking, slotId) => {
  if (!booking?.slots?.length) return "-";

  const slotIndex = booking.slots.findIndex((slot) => slot.id === slotId);
  return `${slotIndex >= 0 ? slotIndex + 1 : "?"}/${booking.slots.length}`;
};

const sortDashboardRows = (a, b) => {
  const aStart = (a.price?.startTime?.hours || 0) * 60 + (a.price?.startTime?.minutes || 0);
  const bStart = (b.price?.startTime?.hours || 0) * 60 + (b.price?.startTime?.minutes || 0);

  if (aStart !== bStart) return aStart - bStart;
  return String(a.unit?.name || "").localeCompare(String(b.unit?.name || ""));
};

const toBookingUpdateData = (booking, overrides = {}) => {
  const { id, customer, ...bookingData } = booking || {};

  return {
    ...bookingData,
    slots: (bookingData.slots || []).map(({ price, unit, ...slot }) => slot),
    ...overrides,
  };
};

function StatusPill({ icon: Icon, label, tone, onClick }) {
  const className = `dashboard-status-pill ${tone}${onClick ? " dashboard-shortcut-pill" : ""}`;

  if (onClick) {
    return (
      <button
        type="button"
        className={className}
        onClick={(event) => {
          event.stopPropagation();
          onClick();
        }}
      >
        <Icon size={16} aria-hidden="true" />
        {label}
      </button>
    );
  }

  return (
    <span className={className}>
      <Icon size={16} aria-hidden="true" />
      {label}
    </span>
  );
}

function FuelChargeForm({
  initialGallons = "",
  initialPricePerGallon = 0,
  priceSourceLabel,
  onSubmit,
  onCancel,
  isSubmitting,
}) {
  const [gallons, setGallons] = useState("");
  const [pricePerGallon, setPricePerGallon] = useState("");

  useEffect(() => {
    setGallons(initialGallons === "" ? "" : String(initialGallons));
  }, [initialGallons]);

  useEffect(() => {
    setPricePerGallon(initialPricePerGallon || initialPricePerGallon === 0 ? String(initialPricePerGallon) : "");
  }, [initialPricePerGallon]);

  const parsedGallons = Number(gallons);
  const parsedPricePerGallon = Number(pricePerGallon);
  const hasGallons = gallons !== "";
  const hasPricePerGallon = pricePerGallon !== "";
  const totalPrice =
    hasGallons && hasPricePerGallon && Number.isFinite(parsedGallons) && Number.isFinite(parsedPricePerGallon)
      ? roundCurrency(parsedGallons * parsedPricePerGallon)
      : 0;
  const canSubmit =
    hasGallons &&
    hasPricePerGallon &&
    Number.isFinite(parsedGallons) &&
    parsedGallons >= 0 &&
    Number.isFinite(parsedPricePerGallon) &&
    parsedPricePerGallon >= 0;

  const submit = (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    onSubmit({
      gallons: roundQuantity(parsedGallons),
      pricePerGallon: roundCurrency(parsedPricePerGallon),
      amount: totalPrice,
    });
  };

  return (
    <form className="dashboard-fuel-form" onSubmit={submit}>
      <div className="dashboard-fuel-grid">
        <div>
          <label htmlFor="dashboardFuelGallons">Gallons</label>
          <input
            id="dashboardFuelGallons"
            className="form-control"
            type="number"
            min="0"
            step="0.01"
            value={gallons}
            onChange={(event) => setGallons(event.target.value)}
            placeholder="0"
          />
        </div>

        <div>
          <label htmlFor="dashboardFuelPrice">Price Per Gallon</label>
          <input
            id="dashboardFuelPrice"
            className="form-control"
            type="number"
            min="0"
            step="0.01"
            value={pricePerGallon}
            onChange={(event) => setPricePerGallon(event.target.value)}
            placeholder="0.00"
          />
          {priceSourceLabel && (
            <div className="dashboard-fuel-source">From {priceSourceLabel}</div>
          )}
        </div>
      </div>

      <div className="dashboard-fuel-total">
        <span>Total Price</span>
        <strong>{formatMoney(totalPrice)}</strong>
      </div>

      <div className="modal-btn-container dashboard-modal-actions">
        <button type="button" className="form-secondary-btn btn" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="form-primary-btn btn" disabled={isSubmitting || !canSubmit}>
          {isSubmitting ? "Saving..." : "Save Fuel Charge"}
        </button>
      </div>
    </form>
  );
}

function Dashboard() {
  const { fetchAvailability } = useAvailability();
  const { getFullBooking } = useBookingData();
  const {
    updateBooking,
    getChecklistTemplates,
    getBookingChecklists,
    createBookingChecklist,
    updateBookingChecklist,
    emailContracts,
  } = useBooking();
  const { retrieveExtras } = useExtras();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedDay, setSelectedDay] = useState(() => getDayFromSearchParams(searchParams));
  const [touchStart, setTouchStart] = useState(null);
  const [activeBookingId, setActiveBookingId] = useState(null);
  const [dashboardModal, setDashboardModal] = useState(null);
  const [fuelIsSaving, setFuelIsSaving] = useState(false);
  const nextDay = selectedDay + DAY_MS;

  const dayFromUrl = useMemo(() => getDayFromSearchParams(searchParams), [searchParams]);

  useEffect(() => {
    setSelectedDay((currentDay) => (currentDay === dayFromUrl ? currentDay : dayFromUrl));
  }, [dayFromUrl]);

  const {
    data: availability,
    isLoading: availabilityIsLoading,
    error: availabilityError,
  } = useQuery(
    ["adminAvailability", selectedDay],
    () =>
      fetchAvailability({
        firstDay: selectedDay,
        lastDay: nextDay,
      }),
    {
      enabled: !!selectedDay,
      staleTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
      cacheTime: 1000 * 60 * 60,
    }
  );

  const {
    data: extras = [],
    isLoading: extrasIsLoading,
    error: extrasError,
  } = useQuery("extras", retrieveExtras, {
    staleTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    cacheTime: 1000 * 60 * 60,
  });

  const {
    data: checklistTemplates = [],
    isLoading: checklistTemplatesIsLoading,
    error: checklistTemplatesError,
  } = useQuery(
    ["checklistTemplates", "active"],
    () => getChecklistTemplates({ active: true }),
    {
      staleTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
      cacheTime: 1000 * 60 * 60,
    }
  );

  const {
    data: activeBookingChecklists = [],
    isLoading: activeBookingChecklistsIsLoading,
    error: activeBookingChecklistsError,
  } = useQuery(
    ["bookingChecklists", activeBookingId],
    () => getBookingChecklists(activeBookingId),
    {
      enabled: dashboardModal === "checklist" && !!activeBookingId,
      staleTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
      cacheTime: 1000 * 60 * 60,
    }
  );

  const bookedSlots = useMemo(() => {
    return (availability ?? [])
      .filter((slot) => slot?.bookingId && slot.status !== "available")
      .sort((a, b) => String(a.unitId || "").localeCompare(String(b.unitId || "")));
  }, [availability]);

  const bookingIds = useMemo(() => {
    return [...new Set(bookedSlots.map((slot) => slot.bookingId).filter(Boolean))];
  }, [bookedSlots]);

  const bookingQueries = useQueries(
    bookingIds.map((bookingId) => ({
      queryKey: ["booking", bookingId],
      queryFn: () => getFullBooking(bookingId),
      enabled: !!bookingId,
      staleTime: 1000 * 60 * 5,
    }))
  );

  const bookingById = new Map();
  bookingIds.forEach((bookingId, index) => {
    if (bookingQueries[index]?.data) {
      bookingById.set(bookingId, { ...bookingQueries[index].data, id: bookingId });
    }
  });

  const dashboardRows = bookedSlots
    .map((slot) => {
      const booking = bookingById.get(slot.bookingId);
      const bookingSlot = booking?.slots?.find((item) => item.id === slot.id);

      return {
        slot,
        booking,
        price: bookingSlot?.price,
        unit: bookingSlot?.unit,
      };
    })
    .sort(sortDashboardRows);

  const activeBooking = activeBookingId
    ? dashboardRows.find((row) => (row.booking?.id || row.slot.bookingId) === activeBookingId)?.booking ||
      bookingById.get(activeBookingId)
    : null;
  const fuelPriceConfig = useMemo(() => getFuelPriceConfig(extras), [extras]);
  const activeFuelCost = activeBooking?.costs?.gas || {};
  const activeFuelPricePerGallon = getFuelPricePerGallon(activeFuelCost, fuelPriceConfig);
  const activeFuelGallons = getFuelGallons(activeFuelCost, activeFuelPricePerGallon);

  const uniqueBookings = [
    ...new Map(
      dashboardRows
        .filter((row) => row.booking)
        .map((row) => [row.booking.id || row.slot.bookingId, row.booking])
    ).values(),
  ];

  const dayPaymentSummary = uniqueBookings.reduce(
    (summary, booking) => {
      const bookingSummary = getPaymentSummary(booking.costs);
      return {
        paid: summary.paid + bookingSummary.paid,
        due: summary.due + bookingSummary.due,
      };
    },
    { paid: 0, due: 0 }
  );
  const todayTimestamp = getTodayTimestamp();
  const isViewingToday = selectedDay === todayTimestamp;

  const isLoading = availabilityIsLoading || bookingQueries.some((query) => query.isLoading);
  const hasBookingError = bookingQueries.some((query) => query.error);

  const syncDaySearchParam = (day) => {
    setSearchParams(
      (currentParams) => {
        const nextParams = new URLSearchParams(currentParams);
        nextParams.set("day", String(day));
        return nextParams;
      },
      { replace: true }
    );
  };

  const setDashboardDay = (day) => {
    setSelectedDay(day);
    syncDaySearchParam(day);
  };

  const changeDay = (days) => {
    setSelectedDay((currentDay) => {
      const newDay = addDays(currentDay, days);
      syncDaySearchParam(newDay);
      return newDay;
    });
  };

  const handleTouchStart = (event) => {
    const touch = event.touches[0];
    setTouchStart({ x: touch.clientX, y: touch.clientY });
  };

  const handleTouchEnd = (event) => {
    if (!touchStart) return;

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - touchStart.x;
    const deltaY = touch.clientY - touchStart.y;

    if (Math.abs(deltaX) > 60 && Math.abs(deltaX) > Math.abs(deltaY)) {
      changeDay(deltaX > 0 ? -1 : 1);
    }

    setTouchStart(null);
  };

  const openBooking = (bookingId) => {
    if (bookingId) {
      navigate(`../booking?booking_id=${bookingId}`);
    }
  };

  const openShortcutModal = (modal, booking) => {
    const bookingId = booking?.id;
    if (!bookingId) return;

    setActiveBookingId(bookingId);
    setDashboardModal(modal);
  };

  const closeShortcutModal = () => {
    setDashboardModal(null);
    setActiveBookingId(null);
  };

  const refreshDashboardBooking = (bookingId) => {
    queryClient.invalidateQueries(["booking", bookingId]);
    queryClient.invalidateQueries(["bookingData", bookingId]);
    queryClient.invalidateQueries(["bookingChecklists", bookingId]);
    queryClient.invalidateQueries(["adminAvailability", selectedDay]);
  };

  const handleQuickNotesSubmit = async (notes) => {
    if (!activeBookingId || !activeBooking) return;

    try {
      await toast.promise(
        updateBooking({
          id: activeBookingId,
          bookingData: toBookingUpdateData(activeBooking, { notes }),
        }),
        {
          pending: "Saving notes...",
          success: "Notes saved.",
          error: "Failed to save notes.",
        }
      );
      refreshDashboardBooking(activeBookingId);
      closeShortcutModal();
    } catch (error) {
      console.error("Error saving dashboard notes:", error);
    }
  };

  const handleQuickSignatureSubmit = async (signatureDataUrl) => {
    if (!activeBookingId || !activeBooking || !signatureDataUrl) return;

    const signatureFields = {
      signature: signatureDataUrl,
      signatureTimestamp: new Date().toISOString(),
      signatureIp: "unknown",
    };

    try {
      const response = await fetch("https://api.ipify.org?format=json");
      const data = await response.json();
      signatureFields.signatureIp = data.ip;
    } catch (error) {
      console.warn("Could not retrieve IP address:", error);
    }

    try {
      await toast.promise(
        updateBooking({
          id: activeBookingId,
          bookingData: toBookingUpdateData(activeBooking, signatureFields),
        }),
        {
          pending: "Saving contract signature...",
          success: "Contract signed.",
          error: "Failed to save contract signature.",
        }
      );

      await toast.promise(
        emailContracts({
          bookingId: activeBookingId,
          signatureDataUrl,
        }),
        {
          pending: "Sending contract signature...",
          success: "Contract signature sent.",
          error: "Failed to send contract signature.",
        }
      );
      
      refreshDashboardBooking(activeBookingId);
      closeShortcutModal();
    } catch (error) {
      console.error("Error saving dashboard signature:", error);
    }
  };

  const handleQuickChecklistSubmit = async (checklistData) => {
    if (!activeBookingId) return;

    const currentChecklist = activeBookingChecklists?.[0];

    try {
      await toast.promise(
        currentChecklist?.id
          ? updateBookingChecklist.mutateAsync({
              id: activeBookingId,
              checklistId: currentChecklist.id,
              checklistData,
            })
          : createBookingChecklist.mutateAsync({
              id: activeBookingId,
              checklistData,
            }),
        {
          pending: "Saving checklist...",
          success: "Checklist saved.",
          error: {
            render({ data }) {
              return data?.response?.data?.message || "Failed to save checklist.";
            },
          },
        }
      );
      refreshDashboardBooking(activeBookingId);
      closeShortcutModal();
    } catch (error) {
      console.error("Error saving dashboard checklist:", error);
    }
  };

  const handleFuelChargeSubmit = async ({ gallons, pricePerGallon, amount }) => {
    if (!activeBookingId || !activeBooking) return;

    const existingGasCost = activeBooking.costs?.gas || {};
    const existingPaid = Number(existingGasCost.paid || 0);
    const costs = {
      ...(activeBooking.costs || {}),
      gas: {
        ...existingGasCost,
        name: existingGasCost.name || "gas",
        gallons,
        pricePerGallon,
        amount,
        paid: Number.isFinite(existingPaid) ? Math.min(existingPaid, amount) : 0,
      },
    };

    try {
      setFuelIsSaving(true);
      await toast.promise(
        updateBooking({
          id: activeBookingId,
          bookingData: toBookingUpdateData(activeBooking, { costs }),
        }),
        {
          pending: "Saving fuel charge...",
          success: "Fuel charge saved.",
          error: "Failed to save fuel charge.",
        }
      );
      refreshDashboardBooking(activeBookingId);
      closeShortcutModal();
    } catch (error) {
      console.error("Error saving fuel charge:", error);
    } finally {
      setFuelIsSaving(false);
    }
  };

  const activeChecklistTemplate = checklistTemplates.find((template) => template.active !== false) || checklistTemplates[0];
  const latestActiveChecklist = activeBookingChecklists?.[0];
  const checklistIsSaving = createBookingChecklist.isLoading || updateBookingChecklist.isLoading;

  return (
    <div className="dashboard-page narrow-container">
      <div
        className="dashboard-shell"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div className="dashboard-day-header main-border">
          <button
            type="button"
            className="dashboard-icon-btn"
            aria-label="Previous day"
            onClick={() => changeDay(-1)}
          >
            <ChevronLeft size={22} aria-hidden="true" />
          </button>

          <div className="dashboard-day-title">
            <div className="dashboard-day-kicker">
              <CalendarDays size={18} aria-hidden="true" />
              Dashboard
            </div>
            <h1>{formatDate(selectedDay)}</h1>
            <button
              type="button"
              className="dashboard-jump-today-btn"
              onClick={() => setDashboardDay(todayTimestamp)}
              disabled={isViewingToday}
            >
              {isViewingToday ? "Viewing Today" : "Jump to Today"}
            </button>
          </div>

          <button
            type="button"
            className="dashboard-icon-btn"
            aria-label="Next day"
            onClick={() => changeDay(1)}
          >
            <ChevronRight size={22} aria-hidden="true" />
          </button>
        </div>

        <div className="dashboard-day-actions">
          <div className="dashboard-day-summary main-border">
            <span>{bookingIds.length} bookings</span>
            <span>{bookedSlots.length} slots</span>
            <span>Paid {formatMoney(dayPaymentSummary.paid)}</span>
            <span className={dayPaymentSummary.due > 0 ? "dashboard-total-due" : "dashboard-total-clear"}>
              Due {formatMoney(dayPaymentSummary.due)}
            </span>
          </div>
        </div>

        {availabilityError && (
          <div className="dashboard-alert">Bookings could not be loaded for this day.</div>
        )}

        {hasBookingError && (
          <div className="dashboard-alert">Some booking details could not be loaded.</div>
        )}

        {extrasError && (
          <div className="dashboard-alert">Add-on details could not be loaded.</div>
        )}

        {isLoading && (
          <div className="dashboard-empty main-border">Loading bookings...</div>
        )}

        {!isLoading && dashboardRows.length === 0 && (
          <div className="dashboard-empty main-border">
            No bookings for {formatShortDate(selectedDay)}.
          </div>
        )}

        {!isLoading && dashboardRows.length > 0 && (
          <div className="dashboard-booking-list">
            {dashboardRows.map(({ slot, booking, unit, price }) => {
              const customer = booking?.customer;
              const payment = getPaymentSummary(booking?.costs);
              const selectedAddons = getSelectedAddons(booking, extras);
              const contractSigned = Boolean(booking?.signature);
              const checklistSigned = isChecklistSigned(booking);
              const phone = customer?.phone || customer?.phoneNumber;

              return (
                <article
                  key={slot.id}
                  className="dashboard-booking-card main-border"
                  role="button"
                  tabIndex={0}
                  onClick={() => openBooking(slot.bookingId)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      openBooking(slot.bookingId);
                    }
                  }}
                >
                  <div className="dashboard-booking-main">
                    <div>
                      <div className="dashboard-unit-line">
                        <span>{unit?.name || "Unit not loaded"}</span>
                        <span>Slot {getSlotPosition(booking, slot.id)}</span>
                      </div>
                      <h2>{helpers.formatCustomerName(customer, "No customer name")}</h2>
                      <a
                        href={phone ? `tel:${phone}` : undefined}
                        className="dashboard-phone-link"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <Phone size={16} aria-hidden="true" />
                        {helpers.formatCustomerPhone(phone, "No phone")}
                      </a>
                    </div>

                    <div className="dashboard-time-block">
                      <Clock size={18} aria-hidden="true" />
                      <span>{getTimeRange(price)}</span>
                    </div>
                  </div>

                  <div className="dashboard-status-row">
                    <StatusPill
                      icon={FileSignature}
                      label={contractSigned ? "Contract Signed" : "Contract Missing"}
                      tone={contractSigned ? "good" : "bad"}
                      onClick={() => openShortcutModal("contract", booking)}
                    />
                    <StatusPill
                      icon={ShieldCheck}
                      label={checklistSigned ? "Checklist Signed" : "Checklist Missing"}
                      tone={checklistSigned ? "good" : "bad"}
                      onClick={() => openShortcutModal("checklist", booking)}
                    />
                    <StatusPill
                      icon={Clock}
                      label={booking?.arrivalTime ? `Arrival ${booking.arrivalTime}` : "Arrival Not Selected"}
                      tone={booking?.arrivalTime ? "neutral" : "warn"}
                    />
                  </div>

                  <div className="dashboard-detail-grid">
                    <div className="dashboard-detail-block">
                      <div className="dashboard-detail-label">
                        <Package size={16} aria-hidden="true" />
                        Add-ons
                      </div>
                      {extrasIsLoading ? (
                        <p>Loading add-ons...</p>
                      ) : selectedAddons.length > 0 ? (
                        <div className="dashboard-chip-list">
                          {selectedAddons.map((addon) => (
                            <span className="dashboard-chip" key={addon}>
                              {formatLabel(addon)}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p>None</p>
                      )}
                      <button
                        type="button"
                        className="dashboard-inline-action"
                        onClick={(event) => {
                          event.stopPropagation();
                          openShortcutModal("fuel", booking);
                        }}
                      >
                        {booking?.costs?.gas ? "Edit Fuel Charge" : "Add Fuel Charge"}
                      </button>
                    </div>

                    <div className="dashboard-detail-block">
                      <div className="dashboard-detail-label">
                        <CreditCard size={16} aria-hidden="true" />
                        Payment
                      </div>
                      <div className="dashboard-payment-line">
                        <span>Paid {formatMoney(payment.paid)}</span>
                        <span className={payment.due > 0 ? "dashboard-amount-due" : ""}>
                          Due {formatMoney(payment.due)}
                        </span>
                      </div>
                    </div>

                    <div className="dashboard-detail-block dashboard-notes-block">
                      <div className="dashboard-detail-label">
                        <User size={16} aria-hidden="true" />
                        Customer Notes
                      </div>
                      <p>{booking?.customerNotes || "None"}</p>
                    </div>

                    <button
                      type="button"
                      className="dashboard-detail-block dashboard-notes-block dashboard-shortcut-block"
                      onClick={(event) => {
                        event.stopPropagation();
                        openShortcutModal("notes", booking);
                      }}
                    >
                      <div className="dashboard-detail-label">
                        <MessageSquare size={16} aria-hidden="true" />
                        Internal Notes
                      </div>
                      <p>{booking?.notes || "None"}</p>
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      <EditModal
        title="Internal Notes"
        show={dashboardModal === "notes"}
        onClose={closeShortcutModal}
      >
        <NotesForm
          initialData={activeBooking?.notes || ""}
          onSubmit={handleQuickNotesSubmit}
        />
      </EditModal>

      <EditModal
        title="Contract"
        show={dashboardModal === "contract"}
        onClose={closeShortcutModal}
        size="xl"
        fullscreen="md-down"
        dialogClassName="contract-modal-dialog"
        bodyClassName="contract-modal-body"
      >
        <ContractModalContent
          bookingId={activeBookingId}
          booking={activeBooking}
          onSignatureSubmit={handleQuickSignatureSubmit}
        />
      </EditModal>

      <EditModal
        title="Checklist"
        show={dashboardModal === "checklist"}
        onClose={closeShortcutModal}
        size="xl"
        dialogClassName="checklist-modal-dialog"
        bodyClassName="checklist-modal-body"
      >
        {checklistTemplatesError || activeBookingChecklistsError ? (
          <div className="dashboard-alert">Checklist could not be loaded.</div>
        ) : checklistTemplatesIsLoading || activeBookingChecklistsIsLoading ? (
          <div className="dashboard-empty">Loading checklist...</div>
        ) : (
          <ChecklistForm
            template={activeChecklistTemplate}
            existingChecklist={latestActiveChecklist}
            customer={activeBooking?.customer}
            onSubmit={handleQuickChecklistSubmit}
            onCancel={closeShortcutModal}
            isSubmitting={checklistIsSaving}
          />
        )}
      </EditModal>

      <EditModal
        title="Fuel Charge"
        show={dashboardModal === "fuel"}
        onClose={closeShortcutModal}
      >
        <FuelChargeForm
          initialGallons={activeFuelGallons}
          initialPricePerGallon={activeFuelPricePerGallon}
          priceSourceLabel={fuelPriceConfig?.name}
          onSubmit={handleFuelChargeSubmit}
          onCancel={closeShortcutModal}
          isSubmitting={fuelIsSaving}
        />
      </EditModal>
    </div>
  );
}

export default Dashboard;
